import { h } from "preact";
import { useState, useMemo, useEffect } from "preact/hooks";
import useProduct from "../../helpers/useProduct/useProduct";
import fetchProduct, {
  getCachedProduct,
} from "../../helpers/useProduct/fetchProduct";
import ProductAddon, { SIZE_OPTION_NAME } from "./product-addon";

const DEFAULT_QUANTITY = 1;

export default function ProductAddons({
  productId,
  idPrefix,
  mainVariant,
  selectedVariants,
  setSelectedVariants,
  addedToCart,
  addonLimit,
}) {
  const [productData] = useProduct(productId);
  const [selectedProducts, setSelectedProducts] = useState({});

  // Keyed by addon product rather than by addon variant, so an addon offered by
  // both the old and the new main variant keeps its component — and so its
  // expanded state and its DOM — across the switch.
  const nextAddons = useMemo(() => {
    const variantAddons = mainVariant?.addOnProductVariants || [];

    const byProduct = new Map();

    if (variantAddons.length) {
      variantAddons
        .filter(({ product }) => product?.id)
        .forEach(({ id, product }) => {
          if (byProduct.has(product.id)) return;

          byProduct.set(product.id, {
            key: product.id,
            productId: product.id,
            defaultVariantId: id,
          });
        });
    } else {
      (productData?.addOnProducts || []).forEach(({ id }) =>
        byProduct.set(id, { key: id, productId: id, defaultVariantId: null }),
      );
    }

    const all = [...byProduct.values()];

    return {
      items: addonLimit != null ? all.slice(0, addonLimit) : all,
      // Snapshotted alongside the list so the size the addons match and the
      // addons themselves always change in the same render.
      matchSize:
        mainVariant?.selectedOptions?.find(
          ({ name }) => name === SIZE_OPTION_NAME,
        )?.value || null,
    };
  }, [mainVariant, productData, addonLimit]);

  // Switching main variants can bring in addon products that haven't been
  // fetched yet, which would render as nothing until they arrive. The previous
  // addons stay on screen until every new one can render in the same frame.
  const [{ items: addons, matchSize }, setAddons] = useState(nextAddons);

  useEffect(() => {
    const pending = nextAddons.items.filter(
      ({ productId: addonProductId }) => !getCachedProduct(addonProductId),
    );

    if (!pending.length) {
      setAddons(nextAddons);
      return;
    }

    let cancelled = false;

    Promise.all(
      pending.map(({ productId: addonProductId }) =>
        fetchProduct(addonProductId),
      ),
    )
      // A failed addon fetch shouldn't strand the shopper on the old variant's
      // list — commit either way and let that addon render nothing.
      .catch(() => {})
      .then(() => {
        if (cancelled) return;
        setAddons(nextAddons);
      });

    return () => {
      cancelled = true;
    };
  }, [nextAddons]);

  const selectedHash = useMemo(
    () =>
      Object.entries(selectedProducts)
        .map(
          ([key, { variant, quantity, added }]) =>
            `${key}-${variant?.id}-${quantity}-${added}`,
        )
        .join("-"),
    [selectedProducts],
  );

  const addonKeys = useMemo(
    () => addons.map(({ key }) => key).join("|"),
    [addons],
  );

  useEffect(() => {
    if (!addedToCart) return;
    setSelectedProducts({});
  }, [addedToCart]);

  // An addon can be configured without being wanted — only the ones toggled on
  // are handed back to the form for the cart. Reading through the current addon
  // list also drops anything configured against an addon the selected variant
  // no longer offers.
  useEffect(() => {
    setSelectedVariants(
      addons
        .map(({ key }) => selectedProducts[key])
        .filter((addon) => addon?.added && addon.variant)
        .map(({ variant, quantity }) => ({
          variant,
          quantity: quantity ?? DEFAULT_QUANTITY,
        })),
    );
  }, [setSelectedVariants, selectedHash, addonKeys]);

  // Updated from the previous state rather than the render's copy: switching
  // main variants has every addon re-resolve its variant at once, and reading a
  // shared snapshot would drop all but the last of those updates.
  const setSelectedDataForProduct = (key, data) => {
    setSelectedProducts((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        ...data,
      },
    }));
  };

  const findQuantityForProduct = (key) => {
    return selectedProducts[key]?.quantity ?? DEFAULT_QUANTITY;
  };

  const findVariantForProduct = (key) => {
    return selectedProducts[key]?.variant || null;
  };

  const findAddedForProduct = (key) => {
    return selectedProducts[key]?.added || false;
  };

  if (!addons.length) return null;

  return addons.map(
    ({ key, productId: addonProductId, defaultVariantId }, i) => (
      <ProductAddon
        key={key}
        productId={addonProductId}
        idPrefix={`${idPrefix}-addon-${i}`}
        defaultVariantId={defaultVariantId}
        matchSize={matchSize}
        selectedVariant={findVariantForProduct(key)}
        setSelectedVariant={(variant) =>
          setSelectedDataForProduct(key, { variant })
        }
        variantQuantity={findQuantityForProduct(key)}
        setVariantQuantity={(quantity) =>
          setSelectedDataForProduct(key, {
            quantity,
            ...(quantity === 0 && { added: false }),
          })
        }
        addedToCart={addedToCart}
        added={findAddedForProduct(key)}
        setAdded={(added) =>
          setSelectedDataForProduct(key, {
            added,
            ...(added &&
              findQuantityForProduct(key) === 0 && {
                quantity: DEFAULT_QUANTITY,
              }),
          })
        }
      />
    ),
  );
}
